import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { LoadingButton } from "../../components/ui/loading-button";

type DeleteConfirmationDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  entityId: number | null;
  entityName: string;
  entityType: "employee" | "team";
};

export default function DeleteConfirmationDialog({
  isOpen,
  onClose,
  entityId,
  entityName,
  entityType,
}: DeleteConfirmationDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!entityId) throw new Error(`${entityType} ID is required`);
      const endpoint = entityType === "employee" 
        ? `/api/employees/${entityId}` 
        : `/api/teams/${entityId}`;
      return apiRequest(endpoint, { method: "DELETE" });
    },
    onSuccess: () => {
      const queryKey = entityType === "employee" ? '/api/employees' : '/api/teams';
      queryClient.invalidateQueries({ queryKey: [queryKey] });
      
      toast({
        title: `${entityType.charAt(0).toUpperCase() + entityType.slice(1)} deleted`,
        description: `${entityName} has been deleted successfully.`,
      });
      
      onClose();
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Failed to delete ${entityType}. Please try again.`,
        variant: "destructive",
      });
    },
  });

  const handleDelete = () => {
    deleteMutation.mutate();
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{`Delete ${entityType.charAt(0).toUpperCase() + entityType.slice(1)}`}</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete <strong>{entityName}</strong>? This action cannot be undone.
            {entityType === "employee" && (
              <div className="mt-2">
                This will also remove this employee from all teams and unassign all licenses.
              </div>
            )}
            {entityType === "team" && (
              <div className="mt-2">
                This will also unassign all licenses from this team. Team members will not be deleted.
              </div>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <LoadingButton
            variant="destructive"
            onClick={handleDelete}
            isLoading={deleteMutation.isPending}
          >
            Delete
          </LoadingButton>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}